import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';

import {
  lidSendBlockedDetail,
  sendMessage,
  type Message,
  type PagedMessages,
} from '@/features/chat/chat.api';
import { mergeDelivery } from '@/features/chat/delivery-status';
import { chatKeys } from '@/features/chat/keys';
import { useAuthStore } from '@/store/auth';

/**
 * Shape of the infinite-query cache value for `chatKeys.messages(id)`.
 * Mirrors what react-query produces for `useInfiniteQuery`.
 */
export interface InfiniteMessages {
  pages: PagedMessages[];
  pageParams: number[];
}

/**
 * Variables for one `useSendMessage.mutate` call.
 *
 *   - `text` and `file` are forwarded to the backend's multipart form.
 *   - `clientRef` is a UUID the caller generated up-front; it is the cache
 *     key for the optimistic bubble and the field the server echoes back
 *     on the persisted `MessageOut`.
 *   - `retryOfMessageId` is the temp id of an optimistic entry the caller
 *     wants to retry. When present we mark that entry "pending" and reuse
 *     the same `clientRef`; on success the in-place patch flips it to the
 *     server's row exactly like the first attempt would.
 *   - `replyTo` is the quoted message's `platform_message_id` (wamid), not
 *     its internal `id`. Forwarded verbatim to `sendMessage` and stamped
 *     onto the optimistic placeholder's `reply_to_platform_message_id` so
 *     the quoted-preview renders immediately, before the server echoes it
 *     back.
 *   - `threadKey` — set when the send originates from an open MERGED
 *     phone-thread (ThreadPanel). Every optimistic write / success patch /
 *     failure mark is applied to `chatKeys.threadMessages(threadKey)` IN
 *     ADDITION to the per-conversation slot, via the exact same helpers —
 *     one merge path, two cache slots. NOT sent to the server (merge is a
 *     read-time view; the POST still targets one conversation).
 *   - `accountId` — the sending account's UUID (the chosen member
 *     conversation's `account_id`), stamped onto the optimistic
 *     placeholder's `account_id` so ThreadPanel's per-bubble account chip
 *     renders immediately. The server's authoritative value overwrites it
 *     on the 200 patch.
 */
export interface SendMessageVariables {
  conversationId: string;
  text?: string | null;
  file?: File | null;
  clientRef: string;
  retryOfMessageId?: string;
  replyTo?: string | null;
  threadKey?: string | null;
  accountId?: string | null;
}

interface SendMutationContext {
  /** Snapshot of the cache before the optimistic write, so onError can
   *  surface a 'failed' marker without losing the original page shape on
   *  a hard rollback path. We don't actually use it for rollback (we want
   *  the bubble to STAY so the user can retry) but it's here for future
   *  cases like a 4xx where the bubble should disappear. */
  previous: InfiniteMessages | undefined;
}

/**
 * Build an optimistic placeholder `Message` from the send variables and the
 * authenticated agent.
 *
 *   - `id` is a temp UUID-like string prefixed `optim-` so it never collides
 *     with a real server id. The real id is patched in on 200.
 *   - `client_ref` is the load-bearing field for the in-place patch — it is
 *     stable across the optimistic → sent lifecycle, and the React key on
 *     `MessageBubble` is derived from it (NOT from `id`).
 *   - `media` is a best-effort preview: when the user attached a file we
 *     synthesize a MediaOut whose `url` is a local `blob:` URL so the
 *     bubble can render a preview before the server returns. On the 200
 *     patch the URL flips to the canonical `/api/v1/media/{id}` path.
 *
 * The optimistic placeholder is intentionally minimal (no `platform_ts`,
 * no server-assigned `platform_message_id`); the in-place patch writes the
 * server's authoritative values on top. `reply_to_platform_message_id` is
 * the one exception — it's stamped from `variables.replyTo` up front so the
 * quoted-preview renders on the optimistic bubble immediately.
 */
export function buildOptimisticMessage(
  variables: SendMessageVariables,
  senderAgentId: string,
): Message {
  const { conversationId, text, file, clientRef, replyTo, accountId } = variables;

  let contentType = 'text';
  if (file) {
    contentType = file.type.startsWith('image/') ? 'image' : 'document';
  }

  return {
    id: `optim-${clientRef}`,
    conversation_id: conversationId,
    // Present only when the caller knows it (ThreadPanel does — it chose
    // the member conversation, so it knows the account). `Message.account_id`
    // is optional exactly for locally-built entries like this one.
    ...(accountId ? { account_id: accountId } : {}),
    direction: 'outbound',
    content_type: contentType,
    content_text: text && text.length > 0 ? text : null,
    media: file
      ? {
          mime: file.type || null,
          size_bytes: file.size,
          filename: file.name,
          // Local blob URL for the preview. Released by the bubble after
          // the server URL takes over (see MessageBubble's effect).
          url: typeof URL !== 'undefined' && 'createObjectURL' in URL
            ? URL.createObjectURL(file)
            : '',
        }
      : null,
    location: null,
    platform_message_id: null,
    reply_to_platform_message_id: replyTo ?? null,
    sender_kind: 'agent',
    sender_agent_id: senderAgentId,
    platform_ts: null,
    created_at: new Date().toISOString(),
    delivery_status: 'pending',
    client_ref: clientRef,
  };
}

/**
 * Merge a server `MessageOut` into an infinite-message cache value.
 *
 *   - If an entry with the same `client_ref` already exists (the
 *     optimistic placeholder, or a previous WS echo), mutate it IN PLACE
 *     — keep the array index, keep the React key (which is `client_ref`),
 *     swap the other fields. This is what guarantees no remount → no
 *     flicker → no scroll jump.
 *   - If the server id is already present (a late WS echo for a message
 *     we already patched), update the row in place — never duplicate.
 *   - Either way the DELIVERY fields merge monotonically (`mergeDelivery`,
 *     2026-09 honest send status — spec 02 §2 P7 / §4.6): the server row
 *     wins everywhere else, but a write computed before a receipt (the send
 *     endpoint's late REST 201 `sent`, or its own `message.new`) never moves
 *     a `delivered` / `read` row back down, and never drops a receipt time
 *     the cache already holds.
 *   - Otherwise, prepend to `pages[0].items` (backend order is newest-
 *     first within a page).
 *
 * Returns a new top-level object so react-query sees the cache changed,
 * but the per-page item arrays are only rebuilt for the page that
 * actually changed — important for `useInfiniteQuery` observers.
 *
 * Exported because the M5-T6 WebSocket `message.new` handler will use the
 * exact same function — there is one true merge path for the chat cache.
 */
export function mergeServerMessage(
  cache: InfiniteMessages | undefined,
  serverMessage: Message,
): InfiniteMessages {
  if (!cache) {
    // No cache yet — synthesize a single-page envelope so a WS arrival
    // before the initial fetch completes still lands somewhere sensible.
    return {
      pages: [
        {
          items: [serverMessage],
          total: 1,
          limit: 50,
          offset: 0,
        },
      ],
      pageParams: [0],
    };
  }

  const clientRef = serverMessage.client_ref;
  let patched = false;
  const newPages = cache.pages.map((page) => {
    if (patched) return page;
    const idx = page.items.findIndex((m) => {
      if (clientRef && m.client_ref === clientRef) return true;
      if (m.id === serverMessage.id) return true;
      return false;
    });
    if (idx === -1) return page;
    patched = true;
    const newItems = page.items.slice();
    newItems[idx] = {
      ...serverMessage,
      ...mergeDelivery(newItems[idx], serverMessage),
    };
    return { ...page, items: newItems };
  });

  if (patched) {
    return { ...cache, pages: newPages };
  }

  // No matching entry — this is a fresh server message (most commonly a
  // brand-new inbound). Prepend to pages[0] (backend order is DESC). If
  // there are no pages at all, fall through to the empty-cache branch.
  if (newPages.length === 0) {
    return {
      ...cache,
      pages: [
        {
          items: [serverMessage],
          total: 1,
          limit: 50,
          offset: 0,
        },
      ],
      pageParams: cache.pageParams.length > 0 ? cache.pageParams : [0],
    };
  }

  const [firstPage, ...rest] = newPages;
  return {
    ...cache,
    pages: [
      {
        ...firstPage,
        items: [serverMessage, ...firstPage.items],
        total: firstPage.total + 1,
      },
      ...rest,
    ],
  };
}

/**
 * Prepend a freshly-built optimistic message to `pages[0].items[0]`
 * (backend order is newest-first DESC), synthesizing a single-page envelope
 * when the slot is empty. Extracted so `onMutate` applies the IDENTICAL
 * write to the per-conversation slot and (thread sends) the merged-thread
 * slot.
 */
function prependOptimistic(
  old: InfiniteMessages | undefined,
  optimistic: Message,
): InfiniteMessages {
  if (!old || old.pages.length === 0) {
    return {
      pages: [
        {
          items: [optimistic],
          total: 1,
          limit: 50,
          offset: 0,
        },
      ],
      pageParams:
        old && old.pageParams.length > 0 ? old.pageParams : [0],
    };
  }
  const [firstPage, ...rest] = old.pages;
  return {
    ...old,
    pages: [
      {
        ...firstPage,
        items: [optimistic, ...firstPage.items],
        total: firstPage.total + 1,
      },
      ...rest,
    ],
  };
}

/**
 * Patch the optimistic entry to `delivery_status: 'failed'` while keeping
 * the same array index and the same React key (`client_ref`). The bubble
 * stays mounted; the user retries with the same `client_ref`.
 */
function markOptimisticFailed(
  cache: InfiniteMessages | undefined,
  clientRef: string,
): InfiniteMessages | undefined {
  if (!cache) return cache;
  let patched = false;
  const newPages = cache.pages.map((page) => {
    if (patched) return page;
    const idx = page.items.findIndex((m) => m.client_ref === clientRef);
    if (idx === -1) return page;
    patched = true;
    const newItems = page.items.slice();
    newItems[idx] = { ...newItems[idx], delivery_status: 'failed' };
    return { ...page, items: newItems };
  });
  return patched ? { ...cache, pages: newPages } : cache;
}

/**
 * Patch the optimistic entry to `delivery_status: 'pending'` (used by
 * retry).
 */
function markOptimisticRetrying(
  cache: InfiniteMessages | undefined,
  clientRef: string,
): InfiniteMessages | undefined {
  if (!cache) return cache;
  let patched = false;
  const newPages = cache.pages.map((page) => {
    if (patched) return page;
    const idx = page.items.findIndex((m) => m.client_ref === clientRef);
    if (idx === -1) return page;
    patched = true;
    const newItems = page.items.slice();
    newItems[idx] = { ...newItems[idx], delivery_status: 'pending' };
    return { ...page, items: newItems };
  });
  return patched ? { ...cache, pages: newPages } : cache;
}

/**
 * Send an outbound message — the THE iron rule.
 *
 *   1. `onMutate`: generate (or accept) a `client_ref`, write an
 *      optimistic Message into `pages[0].items[0]` with `delivery_status:
 *      'pending'`. The React key on the bubble is `client_ref`. Cancel
 *      any in-flight `fetchNextPage` / refetch so the optimistic write
 *      isn't immediately stomped by a stale server response.
 *   2. `mutationFn`: POST the multipart body to the Hub.
 *   3. `onSuccess`: `mergeServerMessage` patches the same array index in
 *      place. The React key is unchanged → DOM node reused → zero
 *      flicker, zero scroll jump.
 *   4. `onError`: `markOptimisticFailed` flips the status to 'failed'
 *      without unmounting; the bubble exposes a Retry button. A 409 (the
 *      Hub's LID send guard or account-not-connected send guard)
 *      ADDITIONALLY surfaces one quiet toast telling the operator why — a
 *      bare failed bubble read as a mysterious send failure.
 *
 * No `invalidateQueries` ever fires for the messages list — that would
 * trigger a refetch that erases the optimistic entry before the server
 * data lands. The cache is the source of truth; the next polled refresh
 * carries the same row anyway.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const agent = useAuthStore((s) => s.agent);

  return useMutation<Message, unknown, SendMessageVariables, SendMutationContext>({
    mutationFn: ({ conversationId, text, file, clientRef, replyTo }) =>
      sendMessage({ conversationId, text, file, clientRef, replyTo }),

    onMutate: async (variables) => {
      const key = chatKeys.messages(variables.conversationId);
      // Every cache slot this send touches: the per-conversation window,
      // plus (thread sends) the merged-thread window. Same write applied to
      // each — the merged thread is a read-time union of its members, so
      // both slots must show the optimistic bubble.
      const keys: QueryKey[] = [key];
      if (variables.threadKey) {
        keys.push(chatKeys.threadMessages(variables.threadKey));
      }

      // Cancel any in-flight messages refetch so the optimistic write isn't
      // immediately overwritten by a stale page that's already on the wire.
      for (const k of keys) {
        await queryClient.cancelQueries({ queryKey: k });
      }

      const previous = queryClient.getQueryData<InfiniteMessages>(key);

      if (variables.retryOfMessageId) {
        // Retry path: mark the existing entry 'pending' again — do NOT
        // build a fresh optimistic message, the bubble is already mounted.
        for (const k of keys) {
          queryClient.setQueryData<InfiniteMessages>(
            k,
            (old) => markOptimisticRetrying(old, variables.clientRef) ?? old,
          );
        }
        return { previous };
      }

      // Fresh send. Build the optimistic message and prepend to pages[0].
      // The senderAgentId fallback to '' is a safety net — the UI never
      // surfaces send without an authenticated agent, but if it somehow
      // did, the empty id collapses to "Sent by agent" in the bubble.
      const optimistic = buildOptimisticMessage(variables, agent?.id ?? '');

      for (const k of keys) {
        queryClient.setQueryData<InfiniteMessages>(k, (old) =>
          prependOptimistic(old, optimistic),
        );
      }

      return { previous };
    },

    onSuccess: (serverMessage, variables) => {
      patchCacheWith(
        queryClient,
        chatKeys.messages(variables.conversationId),
        serverMessage,
      );
      if (variables.threadKey) {
        patchCacheWith(
          queryClient,
          chatKeys.threadMessages(variables.threadKey),
          serverMessage,
        );
      }
    },

    onError: (error, variables) => {
      const keys: QueryKey[] = [chatKeys.messages(variables.conversationId)];
      if (variables.threadKey) {
        keys.push(chatKeys.threadMessages(variables.threadKey));
      }
      for (const k of keys) {
        queryClient.setQueryData<InfiniteMessages>(
          k,
          (old) => markOptimisticFailed(old, variables.clientRef) ?? old,
        );
      }

      // 409 = a Hub send guard rejected the POST (FastAPI `{"detail": ...}`
      // body). Tell the operator WHY, in the console's UI language — the
      // failed bubble alone read as a mysterious send failure. The stable
      // `id` makes a rapid resend burst REPLACE the one toast instead of
      // stacking copies (sonner replaces same-id toasts); the bubble stays
      // failed with its Retry button. Two guards, told apart by the detail:
      //   - LID send guard — the customer is known only by WhatsApp LID and
      //     a console reply would be misrouted; the detail is a ready-made
      //     Chinese instruction (reply from the phone), shown verbatim and
      //     long enough to read.
      //   - otherwise the account-not-connected guard (it may reconnect a
      //     moment later, hence the Retry button).
      if (isAxiosError(error) && error.response?.status === 409) {
        const lidDetail = lidSendBlockedDetail(error);
        if (lidDetail) {
          toast(lidDetail, { id: 'lid-send-blocked', duration: 10_000 });
        } else {
          toast('该账号当前未连接，无法发送', { id: 'send-account-not-connected' });
        }
      }
    },

    // Deliberately NO `onSettled: invalidateQueries`. An invalidate would
    // refetch the messages list which would (for the brief refetch
    // window) replace the in-place-patched cache with the network result
    // — flicker. The cache already holds authoritative data after the
    // 200 patch; the next polled refresh (every 30s in useMessages) is
    // when the server's row formally lands, and `mergeServerMessage`
    // makes that a no-op for any row whose `client_ref` already matched.
  });
}

/**
 * Apply `mergeServerMessage` to one cache slot. Extracted so the M5-T6
 * WebSocket handler can call it the same way without re-implementing.
 * Accepts any `QueryKey` because the SAME merge applies to both the
 * per-conversation slot (`chatKeys.messages`) and the merged-thread slot
 * (`chatKeys.threadMessages`) — both hold the `InfiniteMessages` envelope.
 */
export function patchCacheWith(
  queryClient: QueryClient,
  queryKey: QueryKey,
  serverMessage: Message,
): void {
  // Preserve the slot's pre-patch freshness: a WS/echo patch is an
  // incremental write, NOT a full sync — letting setQueryData's default
  // stamp `dataUpdatedAt = now` kept a GC'd-then-rebuilt partial cache
  // permanently "fresh", so reopening the conversation never refetched
  // the missing history. With the timestamp preserved (0 for a slot
  // synthesized from nothing), the next mount sees stale data and
  // background-refetches the full window.
  const state = queryClient.getQueryState<InfiniteMessages>(queryKey);
  queryClient.setQueryData<InfiniteMessages>(
    queryKey,
    (old) => mergeServerMessage(old, serverMessage),
    { updatedAt: state?.dataUpdatedAt ?? 0 },
  );
}
